import { useState, useEffect } from 'react'

/**
 * Componente personalizado para mostrar el prompt de instalación PWA en iOS/iPad
 * Más robusto que react-ios-pwa-prompt para detectar iPad
 */
export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)

  useEffect(() => {
    // Verificar si estamos en el navegador
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return
    }

    // Detectar iOS/iPad de forma más robusta
    const detectIOS = () => {
      const ua = navigator.userAgent || ''
      const platform = navigator.platform || ''
      const vendor = navigator.vendor || ''
      
      // Detectar iPhone, iPad o iPod directamente
      if (/iPad|iPhone|iPod/i.test(ua)) {
        return true
      }
      
      // Detectar iPad en iOS 13+ (reporta como MacIntel pero con touch)
      if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
        const isApple = vendor.indexOf('Apple') > -1
        const hasSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)
        
        // Si es Safari de Apple con touch, es iPad
        if (isApple && hasSafari) {
          return true
        }
      }
      
      // Detección más permisiva: si tiene touch y es Safari, probablemente es iPad
      if (navigator.maxTouchPoints > 1) {
        const isApple = vendor.indexOf('Apple') > -1
        const hasSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)
        
        if (isApple && hasSafari) {
          return true
        }
      }
      
      return false
    }

    // Verificar si ya está instalada
    const checkInstalled = () => {
      // Modo standalone (instalada)
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        return true
      }
      
      // iOS standalone
      if (window.navigator && window.navigator.standalone === true) {
        return true
      }
      
      return false
    }

    const ios = detectIOS()
    const installed = checkInstalled()
    
    setIsIOS(ios)
    setIsInstalled(installed)

    // Si es iOS/iPad y no está instalada, mostrar el prompt después de un delay
    if (ios && !installed) {
      // Verificar si fue rechazado previamente
      const wasDismissed = typeof localStorage !== 'undefined' && 
                          localStorage.getItem('pwa-install-dismissed') === 'true'
      
      if (!wasDismissed) {
        // Delay para asegurar que el componente esté montado
        const timer = setTimeout(() => {
          setShowPrompt(true)
        }, 2000) // 2 segundos
        
        return () => clearTimeout(timer)
      }
    }
  }, [])

  const handleDismiss = () => {
    setShowPrompt(false)
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('pwa-install-dismissed', 'true')
    }
  }

  // No mostrar si ya está instalada
  if (isInstalled) {
    return null
  }

  // No mostrar si no es iOS/iPad
  if (!isIOS) {
    return null
  }

  // No mostrar si fue rechazado
  const wasDismissed = typeof localStorage !== 'undefined' && 
                      localStorage.getItem('pwa-install-dismissed') === 'true'
  if (wasDismissed) {
    return null
  }

  // No mostrar si no hay prompt activo
  if (!showPrompt) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 animate-slide-up">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-primary-800 rounded-xl flex items-center justify-center">
              <svg
                className="h-10 w-10 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Instalar Artemis
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Instala Artemis en tu iPad para acceder más rápido y usar sin conexión.
            </p>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4 space-y-3">
              <div className="flex items-start">
                <span className="font-bold text-primary-600 dark:text-primary-400 mr-3 flex-shrink-0 text-lg">1.</span>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Toca el botón <strong className="text-gray-900 dark:text-white">Compartir</strong> <span className="text-xl">⎋</span> en la barra inferior de Safari
                </p>
              </div>
              <div className="flex items-start">
                <span className="font-bold text-primary-600 dark:text-primary-400 mr-3 flex-shrink-0 text-lg">2.</span>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Desplázate hacia abajo y selecciona <strong className="text-gray-900 dark:text-white">"Agregar a pantalla de inicio"</strong>
                </p>
              </div>
              <div className="flex items-start">
                <span className="font-bold text-primary-600 dark:text-primary-400 mr-3 flex-shrink-0 text-lg">3.</span>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Toca <strong className="text-gray-900 dark:text-white">"Agregar"</strong> en la esquina superior derecha
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="w-full bg-primary-600 text-white px-4 py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold text-base"
            >
              Entendido
            </button>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Cerrar"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
